import React, { useState, useEffect } from 'react';
import { Users, CreditCard, UserCheck, TrendingUp, AlertCircle, Calendar, DollarSign, Bell, X } from 'lucide-react';
import { enquiryAPI, membersMembershipAPI } from '../../services/api';
import { MembersMembership } from '../../types/api';
import { useNavigate } from 'react-router-dom';
import { useGymBranding } from '../../contexts/GymBrandingContext';

interface Notification {
  id: string;
  type: 'expiring' | 'expired' | 'birthday' | 'payment';
  title: string;
  message: string;
  member?: MembersMembership;
  urgent?: boolean;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { branding } = useGymBranding();
  const gymName = branding?.gymName || 'GymsSync';
  const [stats, setStats] = useState({
    totalEnquiries: 0,
    activeMemberships: 0,
    totalMembers: 0,
    totalRevenue: 0
  });
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [dismissedNotifications, setDismissedNotifications] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [enquiries, memberships] = await Promise.all([
          enquiryAPI.getAll(),
          membersMembershipAPI.getAll(),
        ]);

        const totalRevenue = memberships.reduce((sum, membership) => sum + membership.paidAmount, 0);

        setStats({
          totalEnquiries: enquiries.length,
          activeMemberships: memberships.filter(m => m.isActive).length,
          totalMembers: memberships.length,
          totalRevenue: totalRevenue
        });

        // Generate notifications
        generateNotifications(memberships);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();

    // Auto-refresh every 30 seconds to keep data in sync
    const refreshInterval = setInterval(() => {
      fetchStats();
    }, 30000); // 30 seconds

    // Refresh when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchStats();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const generateNotifications = (memberships: MembersMembership[]) => {
    const now = new Date();
    const notificationsList: Notification[] = [];

    memberships.forEach(membership => {
      const endDate = new Date(membership.endDate);
      const daysUntilExpiry = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      const memberName = `${membership.enquiry?.firstName} ${membership.enquiry?.lastName}`;

      // Expiring Soon (within 5 days) - Reminder only for memberships expiring in 5 days or less
      if (daysUntilExpiry > 0 && daysUntilExpiry <= 5) {
        notificationsList.push({
          id: `expiring-${membership.membersMembershipId}`,
          type: 'expiring',
          title: daysUntilExpiry <= 2 ? '⚠️ Urgent: Membership Expiring Very Soon!' : '⏰ Membership Expiring Soon',
          message: `${memberName}'s membership expires in ${daysUntilExpiry} day${daysUntilExpiry > 1 ? 's' : ''} (${endDate.toLocaleDateString()})`,
          member: membership,
          urgent: daysUntilExpiry <= 2
        });
      }

      // Expired (within last 7 days)
      if (daysUntilExpiry < 0 && daysUntilExpiry >= -7) {
        notificationsList.push({
          id: `expired-${membership.membersMembershipId}`,
          type: 'expired',
          title: '❌ Membership Expired',
          message: `${memberName}'s membership expired ${Math.abs(daysUntilExpiry)} day${Math.abs(daysUntilExpiry) > 1 ? 's' : ''} ago (${endDate.toLocaleDateString()})`,
          member: membership,
          urgent: true
        });
      }

      // Pending Payments - Reminder only if payment due date is within 5 days
      if (membership.remainingAmount > 0 && membership.isActive && membership.nextPaymentDueDate) {
        const dueDate = new Date(membership.nextPaymentDueDate);
        const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        // Show reminder only if payment is due within 5 days
        if (daysUntilDue >= 0 && daysUntilDue <= 5) {
          notificationsList.push({
            id: `payment-${membership.membersMembershipId}`,
            type: 'payment',
            title: daysUntilDue <= 2 ? '💰 Urgent: Payment Due Soon!' : '💰 Pending Payment',
            message: `${memberName} has ₹${membership.remainingAmount.toFixed(2)} pending payment${daysUntilDue > 0 ? ` (Due in ${daysUntilDue} day${daysUntilDue > 1 ? 's' : ''})` : ' (Due today)'}`,
            member: membership,
            urgent: daysUntilDue <= 2
          });
        }
      }

      // Birthday Notifications (within next 7 days)
      if (membership.enquiry?.dateOfBirth) {
        const dob = new Date(membership.enquiry.dateOfBirth);
        const today = new Date();
        const birthdayThisYear = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
        
        // If birthday has passed this year, check next year
        if (birthdayThisYear < today) {
          birthdayThisYear.setFullYear(today.getFullYear() + 1);
        }
        
        const daysUntilBirthday = Math.ceil((birthdayThisYear.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysUntilBirthday >= 0 && daysUntilBirthday <= 7) {
          notificationsList.push({
            id: `birthday-${membership.membersMembershipId}`,
            type: 'birthday',
            title: daysUntilBirthday === 0 ? '🎉 Birthday Today!' : '🎂 Upcoming Birthday',
            message: daysUntilBirthday === 0 
              ? `🎊 ${memberName}'s birthday is today! Wish them a happy birthday!`
              : `${memberName}'s birthday is in ${daysUntilBirthday} day${daysUntilBirthday > 1 ? 's' : ''} (${birthdayThisYear.toLocaleDateString()})`,
            member: membership,
            urgent: daysUntilBirthday === 0
          });
        }
      }
    });

    // Sort by urgency and type
    notificationsList.sort((a, b) => {
      if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
      return a.type.localeCompare(b.type);
    });

    setNotifications(notificationsList);
  };

  const dismissNotification = (id: string) => {
    setDismissedNotifications(prev => new Set(prev).add(id));
  };

  const visibleNotifications = notifications.filter(n => !dismissedNotifications.has(n.id));

  const statCards = [
    {
      title: 'Total Enquiries',
      value: stats.totalEnquiries,
      icon: Users,
      color: '#667eea'
    },
    {
      title: 'Active Memberships',
      value: stats.activeMemberships,
      icon: UserCheck,
      color: '#28a745'
    },
    {
      title: 'Total Members',
      value: stats.totalMembers,
      icon: CreditCard,
      color: '#17a2b8'
    },
    {
      title: 'Total Revenue',
      value: `₹${stats.totalRevenue.toFixed(2)}`,
      icon: TrendingUp,
      color: '#ffc107'
    }
  ];

  if (loading) {
    return (
      <div className="dashboard" style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
        {/* Header Skeleton */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            height: '40px',
            backgroundColor: '#E5E7EB',
            borderRadius: '8px',
            width: '30%',
            marginBottom: '0.5rem',
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }}></div>
          <div style={{
            height: '20px',
            backgroundColor: '#E5E7EB',
            borderRadius: '4px',
            width: '40%',
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }}></div>
        </div>

        {/* Stats Cards Skeleton */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="stat-card" style={{
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              backgroundColor: '#E5E7EB'
            }}>
              <div style={{
                height: '60px',
                backgroundColor: '#D1D5DB',
                borderRadius: '8px'
              }}></div>
            </div>
          ))}
        </div>

        {/* Notifications Skeleton */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            height: '24px',
            backgroundColor: '#E5E7EB',
            borderRadius: '4px',
            width: '30%',
            marginBottom: '1rem',
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }}></div>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{
              height: '80px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              marginBottom: '0.75rem',
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            }}></div>
          ))}
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'expiring':
        return <AlertCircle size={20} />;
      case 'expired':
        return <AlertCircle size={20} />;
      case 'birthday':
        return <Calendar size={20} />;
      case 'payment':
        return <DollarSign size={20} />;
      default:
        return <Bell size={20} />;
    }
  };

  const getNotificationColor = (type: string, urgent?: boolean) => {
    if (urgent) return { bg: '#FEE2E2', border: '#DC2626', text: '#991B1B' };
    
    switch (type) {
      case 'expiring':
        return { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E' };
      case 'expired':
        return { bg: '#FEE2E2', border: '#EF4444', text: '#991B1B' };
      case 'birthday':
        return { bg: '#DBEAFE', border: '#3B82F6', text: '#1E40AF' };
      case 'payment':
        return { bg: '#D1FAE5', border: '#10B981', text: '#065F46' };
      default:
        return { bg: '#F3F4F6', border: '#9CA3AF', text: '#374151' };
    }
  };

  return (
    <div>
      <div className="header">
        <h1 className="header-title">Dashboard</h1>
      </div>

      <div className="stats-grid">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="stat-card">
              <div className="stat-number" style={{ color: stat.color }}>
                {stat.value}
              </div>
              <div className="stat-label">{stat.title}</div>
              <Icon size={24} style={{ color: stat.color, marginTop: '0.5rem' }} />
            </div>
          );
        })}
      </div>

      {/* Daily Reminders - List View */}
      {visibleNotifications.length > 0 && (
        <div style={{ marginBottom: '2rem', marginTop: '2rem' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '0',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid #E5E7EB',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.25)',
                  borderRadius: '50%',
                  padding: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)'
                }}>
                  <Bell size={22} color="white" />
                </div>
                <div>
                  <h2 style={{
                    color: 'white',
                    margin: 0,
                    fontSize: '1.25rem',
                    fontWeight: '700'
                  }}>
                    📋 Today's Reminders
                  </h2>
                  <p style={{
                    color: 'rgba(255,255,255,0.9)',
                    margin: '0.25rem 0 0 0',
                    fontSize: '0.8rem'
                  }}>
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <div style={{
                backgroundColor: 'rgba(255,255,255,0.25)',
                color: 'white',
                borderRadius: '20px',
                padding: '0.4rem 0.9rem',
                fontSize: '0.85rem',
                fontWeight: 'bold',
                backdropFilter: 'blur(10px)'
              }}>
                {visibleNotifications.length} {visibleNotifications.length === 1 ? 'Reminder' : 'Reminders'}
              </div>
            </div>

            {/* Reminders List */}
            <div style={{
              maxHeight: '400px',
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollBehavior: 'smooth'
            }}
            className="reminders-scroll-container"
            >
              {visibleNotifications.map((notification, index) => {
                const colors = getNotificationColor(notification.type, notification.urgent);
                return (
                  <div
                    key={notification.id}
                    style={{
                      borderBottom: index < visibleNotifications.length - 1 ? '1px solid #F3F4F6' : 'none',
                      padding: '1rem 1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'white',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#F9FAFB';
                      e.currentTarget.style.paddingLeft = '1.75rem';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.paddingLeft = '1.5rem';
                    }}
                    onClick={() => {
                      if (notification.member) {
                        navigate('/members', { state: { openMemberId: notification.member.membersMembershipId } });
                      } else {
                        navigate('/members');
                      }
                    }}
                  >
                    {/* Priority Indicator */}
                    <div style={{
                      width: '4px',
                      height: '100%',
                      backgroundColor: colors.border,
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0
                    }} />

                    {/* Icon */}
                    <div style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      padding: '0.65rem',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {getNotificationIcon(notification.type)}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.25rem',
                        flexWrap: 'wrap'
                      }}>
                        <h3 style={{
                          margin: 0,
                          fontSize: '0.95rem',
                          fontWeight: '700',
                          color: '#1F2937',
                          lineHeight: '1.4'
                        }}>
                          {notification.title.replace(/[🎉🎂⏰⚠️❌💰📌]/g, '').trim()}
                        </h3>
                        {notification.urgent && (
                          <span style={{
                            backgroundColor: '#FEE2E2',
                            color: '#DC2626',
                            fontSize: '0.65rem',
                            fontWeight: '700',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.03em'
                          }}>
                            HIGH PRIORITY
                          </span>
                        )}
                      </div>
                      <p style={{
                        margin: 0,
                        fontSize: '0.85rem',
                        color: '#6B7280',
                        lineHeight: '1.5'
                      }}>
                        {notification.message}
                      </p>
                    </div>

                    {/* Action Hint */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      flexShrink: 0
                    }}>
                      <span style={{
                        fontSize: '0.75rem',
                        color: '#9CA3AF',
                        fontStyle: 'italic',
                        whiteSpace: 'nowrap'
                      }}>
                        Click to view
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          dismissNotification(notification.id);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#9CA3AF',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          borderRadius: '4px',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#F3F4F6';
                          e.currentTarget.style.color = '#374151';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#9CA3AF';
                        }}
                        title="Dismiss reminder"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div style={{
              background: '#F9FAFB',
              padding: '0.75rem 1.5rem',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.75rem',
              color: '#6B7280'
            }}>
              <span>
                💡 <strong>Tip:</strong> Click on any reminder to view full member details
              </span>
              <span style={{ fontStyle: 'italic' }}>
                Sorted by priority
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Welcome to {gymName}</h2>
        </div>
        <div className="card-body">
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Manage your gym operations efficiently with our comprehensive management system.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: '#333' }}>📋 Enquiries</h3>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                Track potential members from initial inquiry to membership conversion.
              </p>
            </div>
            
            <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: '#333' }}>💳 Membership Plans</h3>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                Create and manage different gym membership options and pricing.
              </p>
            </div>
            
            <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: '#333' }}>👥 Members</h3>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                Manage active members, track payments, and monitor membership status.
              </p>
            </div>
            
            <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: '#333' }}>⚙️ Users</h3>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                Manage gym staff accounts and user permissions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
